/* eslint-disable react-hooks/exhaustive-deps */
import { useEffect, useRef } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { useAppContext } from "../AppContext";
import { fetchCompareProducts } from "../services/productService";
import { getProductId } from "../utils";
import {
  getCompareIds,
  normalizeCompareResult,
  reconcileCartPrices,
} from "../utils/cartPricing";

export const useCartPriceSync = ({ enabled = true } = {}) => {
  const { cartItems, updateCart } = useAppContext();
  const queryClient = useQueryClient();

  // Prices confirmed by the server, with the moment we asked. A list fetched
  // after that confirmation is newer, so it is allowed to win.
  const confirmedRef = useRef({ at: 0, byId: {} });

  const cartProductIdsKey = cartItems
    .map((item) => getProductId(item))
    .join(",");

  // Every cached product query holds the old price, not just the global list:
  // the grids behind the cart read from ["products","infinite"],
  // ["products","category",id], ["products","trending"] and the paginated
  // ["products",{page,size,search}]. Keys match by prefix, so invalidating the
  // bare ["products"] / ["bundles"] roots covers all of them and a price change
  // shows up everywhere at once instead of only in the cart.
  const refreshProductCaches = () => {
    queryClient.invalidateQueries({ queryKey: ["products"] });
    queryClient.invalidateQueries({ queryKey: ["bundles"] });
  };

  const rememberConfirmedPrices = (repricedCart, products, boxes) => {
    const byId = {};

    repricedCart.forEach((item) => {
      const id = getProductId(item);

      if (item?.productName) {
        const product = products?.find((p) => getProductId(p) === id);
        // Only record what the server actually priced, so an item it didn't
        // return isn't frozen at whatever localStorage happened to hold.
        if (product?.price === undefined || product?.price === null) return;
        byId[id] = {
          price: item.price,
          discountPrice: item.discountPrice,
          discountName: item.discountName,
        };
      } else if (item?.name) {
        const box = boxes?.find((b) => getProductId(b) === id);
        if (box?.amount === undefined || box?.amount === null) return;
        byId[id] = { price: item.price, amount: item.amount };
      }
    });

    confirmedRef.current = { at: Date.now(), byId };
  };

  useEffect(() => {
    if (!enabled || cartItems.length === 0) return;

    let cancelled = false;

    (async () => {
      try {
        const compareResult = await fetchCompareProducts(
          getCompareIds(cartItems),
        );
        if (cancelled) return;

        const { products, boxes } = normalizeCompareResult(compareResult);
        const { repricedCart, priceChanges } = reconcileCartPrices(
          cartItems,
          products,
          boxes,
        );

        rememberConfirmedPrices(repricedCart, products, boxes);
        if (priceChanges.length === 0) return;

        updateCart(repricedCart);
        refreshProductCaches();
      } catch (err) {
        // Browsing shouldn't break on a failed refresh — checkout re-validates.
        console.error("Cart price refresh failed", err);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [enabled, cartProductIdsKey]);

  const priceOverrideFor = (item, listUpdatedAt) => {
    const confirmed = confirmedRef.current.byId[getProductId(item)];
    if (!confirmed) return {};
    return (listUpdatedAt || 0) <= confirmedRef.current.at ? confirmed : {};
  };

  return { priceOverrideFor, refreshProductCaches };
};

export default useCartPriceSync;
